import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Alert,
    CircularProgress,
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Save as SaveIcon,
    SwapHoriz as ReassignIcon,
    Edit as EditIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import userListingService from '../../services/userListingService';

/**
 * Page d'édition d'une relation User ↔ Listing
 */
const UserListingEditPage = () => {
    const { userId, listingId } = useParams();
    const navigate = useNavigate();
    
    // États principaux
    const [user, setUser] = useState(null);
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // États de formulaire
    const [listingStatus, setListingStatus] = useState('');
    const [validationNotes, setValidationNotes] = useState('');
    
    // États de réassignation
    const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
    const [newOwner, setNewOwner] = useState(null);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [reassignReason, setReassignReason] = useState('');

    const statusOptions = [
        { value: 'active', label: 'Active', color: 'success' },
        { value: 'pending_verification', label: 'En attente de vérification', color: 'warning' },
        { value: 'rejected', label: 'Rejetée', color: 'error' },
        { value: 'inactive', label: 'Inactive', color: 'default' },
        { value: 'draft', label: 'Brouillon', color: 'info' },
        { value: 'expired', label: 'Expirée', color: 'default' }
    ];

    /**
     * Charger les détails au montage
     */
    useEffect(() => {
        const loadDetails = async () => {
            try {
                setLoading(true);
                setError(null);

                // Charger les détails de l'utilisateur et ses annonces
                const userResponse = await userListingService.getUserListingsByUserId(userId);
                if (userResponse.success) {
                    setUser(userResponse.user);
                    
                    // Trouver l'annonce spécifique
                    const targetListing = userResponse.listings.find(l => l.id === listingId);
                    if (targetListing) {
                        setListing(targetListing);
                        setListingStatus(targetListing.status);
                        setValidationNotes(targetListing.validation_notes || '');
                    } else {
                        setError('Annonce non trouvée');
                    }
                } else {
                    setError('Utilisateur non trouvé');
                }
            } catch (err) {
                console.error('Erreur lors du chargement:', err);
                setError('Erreur lors du chargement des détails');
            } finally {
                setLoading(false);
            }
        };

        if (userId && listingId) {
            loadDetails();
        }
    }, [userId, listingId]);

    /**
     * Charger les utilisateurs disponibles pour la réassignation
     */
    const loadAvailableUsers = async () => {
        try {
            // TODO: Créer un endpoint pour récupérer tous les utilisateurs
            // Pour l'instant, utilisation d'une liste factice
            setAvailableUsers([
                { id: '1', name: 'Utilisateur 1', email: 'user1@example.com' },
                { id: '2', name: 'Utilisateur 2', email: 'user2@example.com' },
                { id: '3', name: 'Utilisateur 3', email: 'user3@example.com' }
            ]);
        } catch (err) {
            console.error('Erreur lors du chargement des utilisateurs:', err);
        }
    };

    /**
     * Sauvegarder les modifications de statut
     */
    const handleSaveChanges = async () => {
        try {
            setSaving(true);
            setError(null);

            // Préparer les données de modification - utilisons la méthode simple updateUserListing
            const updateData = {
                status: listingStatus,
                validation_notes: validationNotes || 'Modification du statut par l\'administration'
            };

            console.log('Sauvegarde des modifications avec les données:', updateData);

            // Utiliser la fonction updateUserListing au lieu de bulkActionUserListings
            const response = await userListingService.updateUserListing(userId, listingId, updateData);

            if (response.success) {
                setSuccess('Modifications sauvegardées avec succès');
                // Actualiser les données
                setListing(prev => ({
                    ...prev,
                    status: listingStatus,
                    validation_notes: validationNotes
                }));
            } else {
                setError('Erreur lors de la sauvegarde');
            }
        } catch (err) {
            console.error('Erreur lors de la sauvegarde:', err);
            setError('Erreur lors de la sauvegarde des modifications');
        } finally {
            setSaving(false);
        }
    };

    /**
     * Ouvrir le dialogue de réassignation
     */
    const handleOpenReassignDialog = () => {
        loadAvailableUsers();
        setReassignDialogOpen(true);
    };

    /**
     * Effectuer la réassignation
     */
    const handleReassignListing = async () => {
        if (!newOwner || !reassignReason.trim()) {
            setError('Veuillez sélectionner un nouveau propriétaire et fournir une raison');
            return;
        }

        try {
            setSaving(true);
            setError(null);

            const response = await userListingService.reassignListing(listingId, {
                newOwnerId: newOwner.id,
                reason: reassignReason
            });

            if (response.success) {
                setSuccess('Annonce réassignée avec succès');
                setReassignDialogOpen(false);
                // Rediriger vers la nouvelle relation
                setTimeout(() => {
                    navigate(`/admin/user-listings/${newOwner.id}/${listingId}`);
                }, 2000);
            } else {
                setError('Erreur lors de la réassignation');
            }
        } catch (err) {
            console.error('Erreur lors de la réassignation:', err);
            setError('Erreur lors de la réassignation de l\'annonce');
        } finally {
            setSaving(false);
        }
    };

    /**
     * Obtenir la couleur du statut
     */
    const getStatusColor = (status) => {
        const option = statusOptions.find(opt => opt.value === status);
        return option?.color || 'primary';
    };

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error && !user && !listing) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button
                    variant="contained"
                    startIcon={<BackIcon />}
                    onClick={() => navigate('/admin/user-listings')}
                >
                    Retour à la liste
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <PageHeader
                title="Éditer la Relation"
                subtitle={`${user?.name} ↔ ${listing?.title}`}
                icon={<EditIcon />}
            />

            {/* Messages d'état */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            {/* Boutons d'actions */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                <Button
                    variant="outlined"
                    startIcon={<BackIcon />}
                    onClick={() => navigate(`/admin/user-listings/${userId}/${listingId}`)}
                >
                    Retour aux détails
                </Button>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveChanges}
                    disabled={saving}
                >
                    {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
                <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<ReassignIcon />}
                    onClick={handleOpenReassignDialog}
                >
                    Réassigner
                </Button>
            </Box>

            <Grid container spacing={3}>
                {/* Informations actuelles */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Informations Actuelles
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>Propriétaire</Typography>
                            <Typography variant="body1">{user?.name}</Typography>
                            <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>Annonce</Typography>
                            <Typography variant="body1">{listing?.title}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                ID: {listing?.id}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>Statut Actuel</Typography>
                            <Chip
                                label={listing?.status}
                                color={getStatusColor(listing?.status)}
                                size="small"
                            />
                        </Box>

                        {listing?.validation_notes && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>Notes de Validation</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {listing.validation_notes}
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>

                {/* Formulaire d'édition */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Modifier les Paramètres
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Statut de l'annonce</InputLabel>
                                    <Select
                                        value={listingStatus}
                                        label="Statut de l'annonce"
                                        onChange={(e) => setListingStatus(e.target.value)}
                                    >
                                        {statusOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip
                                                        label={option.label}
                                                        color={option.color}
                                                        size="small"
                                                    />
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label="Notes de validation"
                                    value={validationNotes}
                                    onChange={(e) => setValidationNotes(e.target.value)}
                                    placeholder="Ajoutez des notes sur cette modification..."
                                    helperText="Ces notes seront visibles dans l'historique des modifications"
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>

            {/* Dialogue de réassignation */}
            <Dialog 
                open={reassignDialogOpen} 
                onClose={() => setReassignDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="warning" />
                    Réassigner l'annonce
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 3 }}>
                        Vous êtes sur le point de réassigner cette annonce à un autre utilisateur. 
                        Cette action ne peut pas être annulée.
                    </DialogContentText>
                    
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={availableUsers}
                                getOptionLabel={(option) => `${option.name} (${option.email})`}
                                value={newOwner}
                                onChange={(event, newValue) => setNewOwner(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Nouveau propriétaire"
                                        placeholder="Sélectionnez un utilisateur..."
                                        required
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Raison de la réassignation"
                                value={reassignReason}
                                onChange={(e) => setReassignReason(e.target.value)}
                                placeholder="Expliquez pourquoi cette annonce est réassignée..."
                                required
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReassignDialogOpen(false)}>
                        Annuler
                    </Button>
                    <Button 
                        onClick={handleReassignListing}
                        variant="contained"
                        color="warning"
                        disabled={!newOwner || !reassignReason.trim() || saving}
                    >
                        {saving ? 'Réassignation...' : 'Confirmer la réassignation'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default UserListingEditPage;
